import { useMemo } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { fmt, type Column } from "@racoon/ui";
import type { TruckRow } from "./adapt-trucks-by-provider";
import { govTruckModelLabel } from "./gov-trucks-filter";

/** Design: data cells are Inter Regular 14/16, Text/body, tracking tight. */
const CELL = "text-sm font-normal leading-4 tracking-tight text-text-body";

/** Shown wherever the Trucks RPC carries no value for a column. */
const EMPTY = <span className="text-sm font-normal leading-4 text-text-secondary">—</span>;

export type TruckColumnActions = {
  /** Opens the truck's record page. Wired to the plate-number link. */
  onDetails: (truckId: string) => void;
};

/**
 * Columns for the desktop trucks registry, following the Figma table
 * (node 18705:11251) including its px widths.
 *
 * Only the columns the Trucks RPC can actually fill are live. The rest are
 * commented out below rather than deleted — the design specifies them, and each
 * needs a backend field before it shows anything but a dash:
 *   - eco class / engine type live on `truck_type`, which stage rarely populates
 *   - garage (depot) only comes back from `GetTruckStatsById`, not the list RPC
 *   - last shift has no field at all
 *   - the gas-analyzer verdict isn't derivable from `last_emission_record`,
 *     which holds raw CO/CO2/O2 percentages rather than a compliance date
 *
 * Every accessor tolerates a missing `truck`: group-header rows share this row
 * type. Their markup replaces the whole `<tr>`, but sorting still walks them.
 */
export const useTruckColumns = ({ onDetails }: TruckColumnActions): Column<TruckRow>[] => {
  const t = useT();

  return useMemo<Column<TruckRow>[]>(
    () => [
      {
        id: "plateNumber",
        header: t(Namespace.Govdash, "trucksTable.plateNumber"),
        accessor: (row) => row.truck?.truck?.plateNumber ?? "",
        sortable: true,
        width: 120,
        // The row itself is inert; this link is the only way into the record.
        cell: ({ row }) => {
          const truck = row.original.truck?.truck;
          if (!truck?.plateNumber || truck.id == null) return EMPTY;
          return (
            <button
              type="button"
              onClick={() => onDetails(String(truck.id))}
              className="rounded text-sm font-medium leading-4 tracking-tight text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:decoration-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
            >
              {truck.plateNumber}
            </button>
          );
        },
      },
      {
        id: "model",
        header: t(Namespace.Govdash, "trucksTable.model"),
        accessor: (row) => truckModel(row) ?? "",
        sortable: true,
        width: 300,
        cell: ({ row }) => {
          const model = truckModel(row.original);
          return model ? <span className={CELL}>{model}</span> : EMPTY;
        },
      },
      {
        id: "year",
        header: t(Namespace.Govdash, "trucksTable.year"),
        accessor: (row) => row.truck?.truck?.year ?? 0,
        sortable: true,
        width: 120,
        // Design centres the year in its cell but keeps the header left-aligned,
        // and this DataTable's `align` only reaches the header — so centre here.
        cell: ({ row }) => {
          const year = row.original.truck?.truck?.year;
          return (
            <div className="flex w-full justify-center">
              {year ? <span className={CELL}>{year}</span> : EMPTY}
            </div>
          );
        },
      },
      // {
      //   id: "ecoClass",
      //   header: t(Namespace.Govdash, "trucksTable.ecoClass"),
      //   accessor: (row) => row.truck?.truck?.truckType?.euroType ?? 0,
      //   sortable: true,
      //   width: 120,
      //   cell: ({ row }) => {
      //     const euroType = row.original.truck?.truck?.truckType?.euroType;
      //     if (!euroType) return EMPTY;
      //     const key =
      //       `TRUCK_STANDARD_${ConnectProto.TruckStandard[euroType]}` as KeysOf<Namespace.Enum>;
      //     return <span className={CELL}>{t(Namespace.Enum, key)}</span>;
      //   },
      // },
      // {
      //   id: "engineType",
      //   header: t(Namespace.Govdash, "trucksTable.engineType"),
      //   accessor: (row) => row.truck?.truck?.truckType?.engineType ?? 0,
      //   sortable: true,
      //   width: 160,
      //   cell: ({ row }) => {
      //     const engineType = row.original.truck?.truck?.truckType?.engineType;
      //     if (!engineType) return EMPTY;
      //     const key =
      //       `TRUCK_ENGINE_TYPE_${ConnectProto.TruckEngineType[engineType]}` as KeysOf<Namespace.Enum>;
      //     return <span className={CELL}>{t(Namespace.Enum, key)}</span>;
      //   },
      // },
      {
        // `GovTruckInfo.depot` resolves the truck's own depot_id. Unset means no
        // depot assigned — it is deliberately not inferred from the owner org.
        id: "garage",
        header: t(Namespace.Govdash, "trucksTable.garage"),
        accessor: (row) => row.truck?.depot?.name ?? "",
        sortable: true,
        width: 200,
        cell: ({ row }) => {
          const name = row.original.truck?.depot?.name;
          return name ? <span className={CELL}>{name}</span> : EMPTY;
        },
      },
      {
        id: "lastShift",
        header: t(Namespace.Govdash, "trucksTable.lastShift"),
        // Sort on the raw instant: the rendered label leads with the time, so
        // sorting the text would order 04:00 before 17:30 regardless of date.
        accessor: (row) => fromTimestamp(row.truck?.lastDriverOnShift?.startedAt)?.getTime() ?? 0,
        sortable: true,
        width: 160,
        cell: ({ row }) => {
          const label = formatLastShiftStart(row.original);
          return label ? <span className={CELL}>{label}</span> : EMPTY;
        },
      },
      // {
      //   // `last_emission_record` carries raw CO/CO2/O2 percentages, not the
      //   // "compliant until <date>" verdict the design shows.
      //   id: "gasAnalyzer",
      //   header: t(Namespace.Govdash, "trucksTable.gasAnalyzer"),
      //   sortable: false,
      //   cell: () => EMPTY,
      // },
    ],
    [t, onDetails],
  );
};

/** Start of a truck's last shift, as the design shows it: "04:00 18 авг. 2026". */
function formatLastShiftStart(row: TruckRow): string | null {
  const startedAt = fromTimestamp(row.truck?.lastDriverOnShift?.startedAt);
  return startedAt ? fmt.dateTime(startedAt, "HH:mm dd MMM yyyy") : null;
}

/** Design shows "Iveco EuroCargo SB-800" — manufacturer plus model. */
function truckModel(row: TruckRow): string {
  return govTruckModelLabel(row.truck);
}
